import React, { useState, useEffect } from "react";
import { MdLocationOn, MdPhone, MdStorefront, MdVerified, MdBusiness } from "react-icons/md";
import axios from "axios";
import { MdLanguage } from "react-icons/md";

// ─── API base ────────────────────────────────────────────────────────────────

const PUBLIC_BASE = "https://admin.greenheapgold.com/public/api/v1";

// ─── Fallback jewellery images for cards without an avatar ───────────────────
const CARD_IMAGES = [
  "https://images.unsplash.com/photo-1610375461246-83df859d849d?w=600&q=80",
  "https://images.unsplash.com/photo-1573408301185-9146fe634ad0?w=600&q=80",
  "https://images.unsplash.com/photo-1584467541268-b040f83be3fd?w=600&q=80",
  "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=600&q=80",
  "https://images.unsplash.com/photo-1601821765780-754fa98637be?w=600&q=80",
  "https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=600&q=80",
  "https://images.unsplash.com/photo-1602751584552-8ba73aad10e1?w=600&q=80",
  "https://images.unsplash.com/photo-1506630448388-4e683c67ddb0?w=600&q=80",
];

// ─── Component ────────────────────────────────────────────────────────────────
export default function FindFranchise() {
  const [states,          setStates]          = useState([]);
  const [cities,          setCities]          = useState([]);
  const [franchises,      setFranchises]      = useState([]);
  const [selectedState,   setSelectedState]   = useState("");  // state id
  const [selectedCity,    setSelectedCity]    = useState("");  // city id
  const [currentPage,     setCurrentPage]     = useState(1);
  const [pagination,      setPagination]      = useState(null);
  const [statesLoading,   setStatesLoading]   = useState(false);
  const [citiesLoading,   setCitiesLoading]   = useState(false);
  const [franchisesLoading, setFranchisesLoading] = useState(false);

  // ── Fetch states on mount ──────────────────────────────────────────────────
  useEffect(() => {
    setStatesLoading(true);
    axios
      .get(`${PUBLIC_BASE}/states`)
      .then((res) => { if (res?.data?.status) setStates(res.data.data || []); })
      .catch((err) => console.error("States fetch error:", err))
      .finally(() => setStatesLoading(false));
  }, []);

  // ── Fetch cities when state changes ───────────────────────────────────────
  useEffect(() => {
    if (!selectedState) { setCities([]); setCitiesLoading(false); return; }
    setCitiesLoading(true);
    setCities([]);
    setSelectedCity("");
    setFranchises([]);
    axios
      .get(`${PUBLIC_BASE}/cities?state_id=${selectedState}`)
      .then((res) => { if (res?.data?.status) setCities(res.data.data || []); })
      .catch((err) => console.error("Cities fetch error:", err))
      .finally(() => setCitiesLoading(false));
  }, [selectedState]);

  // ── Fetch franchises when State (and optionally City) are selected ────────
  useEffect(() => {
    if (!selectedState) { setFranchises([]); setPagination(null); return; }
    setFranchisesLoading(true);

    let url = `${PUBLIC_BASE}/franchises/get?state=${selectedState}&page=${currentPage}&per_page=9`;
    if (selectedCity) url += `&city=${selectedCity}`;

    axios
      .get(url)
      .then((res) => {
        if (res?.data?.status) {
          setFranchises(res.data.data || []);
          setPagination(res.data.pagination || null);
        }
      })
      .catch((err) => console.error("Franchises fetch error:", err))
      .finally(() => setFranchisesLoading(false));
  }, [selectedState, selectedCity, currentPage]);

  const handleStateChange = (e) => {
    setSelectedState(e.target.value);
    setSelectedCity("");
    setFranchises([]);
    setCurrentPage(1);
  };

  const handleCityChange = (e) => {
    setSelectedCity(e.target.value);
    setCurrentPage(1);
  };

  return (
    <>
      <style>{`
        /* ── Page wrapper ─────────────────────────────────────────── */
        .ff-page {
          min-height: 100vh;
          background: #fff8f0;
          padding-bottom: 60px;
        }

        /* ── Hero banner ─────────────────────────────────────────── */
        .ff-hero {
          position: relative;
          padding: 100px 20px 110px;
          text-align: center;
          overflow: hidden;
          min-height: 340px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        .ff-hero-bg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          z-index: 0;
        }
        .ff-hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to bottom,
            rgba(20, 10, 0, 0.62) 0%,
            rgba(90, 20, 0, 0.72) 60%,
            rgba(20, 10, 0, 0.80) 100%
          );
          z-index: 1;
        }
        .ff-hero-content {
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .ff-hero-icon {
          width: 68px;
          height: 68px;
          background: rgba(255,255,255,0.18);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          backdrop-filter: blur(6px);
          border: 1.5px solid rgba(255,215,0,0.45);
          box-shadow: 0 4px 24px rgba(255,215,0,0.15);
        }
        .ff-hero h1 {
          font-size: clamp(1.9rem, 4.5vw, 3.2rem);
          color: #fff;
          font-weight: 800;
          margin: 0 0 14px;
          letter-spacing: -0.5px;
          text-shadow: 0 2px 16px rgba(0,0,0,0.55);
        }
        .ff-hero h1 span {
          color: #ffd700;
        }
        .ff-hero p {
          color: rgba(255,255,255,0.88);
          font-size: 1.05rem;
          max-width: 540px;
          margin: 0 auto;
          line-height: 1.65;
          text-shadow: 0 1px 8px rgba(0,0,0,0.45);
        }

        /* ── Filter card ─────────────────────────────────────────── */
        .ff-filter-card {
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 8px 40px rgba(0,0,0,0.12);
          padding: 28px 32px;
          max-width: 760px;
          margin: -36px auto 40px;
          position: relative;
          z-index: 10;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }
        @media (max-width: 576px) {
          .ff-filter-card { grid-template-columns: 1fr; padding: 20px 16px; margin: -28px 16px 32px; }
        }
        .ff-filter-label {
          display: block;
          font-size: 0.78rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: #7b0000;
          margin-bottom: 8px;
        }
        .ff-select {
          width: 100%;
          padding: 12px 16px;
          border: 1.5px solid #e0cfc8;
          border-radius: 10px;
          font-size: 0.95rem;
          color: #333;
          background: #fffaf7;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%237b0000' d='M6 8L0 0h12z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 14px center;
          cursor: pointer;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .ff-select:focus {
          outline: none;
          border-color: #b8001f;
          box-shadow: 0 0 0 3px rgba(184,0,31,0.12);
        }
        .ff-select:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* ── Section header ──────────────────────────────────────── */
        .ff-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          max-width: 1100px;
          margin: 0 auto 24px;
          padding: 0 16px;
          flex-wrap: wrap;
          gap: 8px;
        }
        .ff-section-title {
          font-size: 1.3rem;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0;
        }
        .ff-count-badge {
          background: #7b0000;
          color: #fff;
          font-size: 0.78rem;
          font-weight: 700;
          padding: 4px 12px;
          border-radius: 20px;
        }

        /* ── Cards grid ──────────────────────────────────────────── */
        .ff-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 24px;
          max-width: 1100px;
          margin: 0 auto;
          padding: 0 16px;
        }

        /* ── Individual card ─────────────────────────────────────── */
        .ff-card {
          background: #fff;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0,0,0,0.07);
          transition: transform 0.25s ease, box-shadow 0.25s ease;
          border: 1px solid #f0e8e2;
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .ff-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 12px 36px rgba(123,0,0,0.15);
        }
        .ff-card-img-wrap {
          position: relative;
          height: 180px;
          overflow: hidden;
          padding: 16px 16px 0 16px;
          box-sizing: border-box;
        }
        .ff-card-img-wrap img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 12px;
          transition: transform 0.4s ease;
          background: #fff;
        }
        .ff-card:hover .ff-card-img-wrap img {
          transform: scale(1.06);
        }
        .ff-logo-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #fff8f0 0%, #fdecd6 100%);
          border-radius: 12px;
        }
        .ff-logo-img {
          width: 110px;
          height: auto;
          object-fit: contain;
          opacity: 0.85;
          filter: drop-shadow(0 2px 8px rgba(123,0,0,0.1));
        }
        .ff-city-pill {
          position: absolute;
          top: 12px;
          left: 12px;
          background: rgba(123,0,0,0.85);
          color: #fff;
          font-size: 0.72rem;
          font-weight: 700;
          padding: 4px 10px;
          border-radius: 20px;
          backdrop-filter: blur(4px);
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }
        .ff-card-body {
          padding: 18px 20px 20px;
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .ff-card-name {
          font-size: 1.05rem;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 12px;
          display: flex;
          align-items: center;
          gap: 6px;
          min-height: 2.6rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          line-height: 1.3;
        }
        .ff-card-name svg {
          color: #b8001f;
          flex-shrink: 0;
        }
        .ff-owner-name {
          font-size: 0.8rem;
          color: #888;
          margin: -6px 0 14px;
          font-style: italic;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ff-verified-badge {
          position: absolute;
          bottom: 12px;
          right: 12px;
          background: rgba(34,139,34,0.88);
          color: #fff;
          font-size: 0.7rem;
          font-weight: 700;
          padding: 3px 9px;
          border-radius: 20px;
          backdrop-filter: blur(4px);
          letter-spacing: 0.03em;
        }
        .ff-info-row {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          margin-bottom: 18px;
          color: #555;
          font-size: 0.86rem;
          line-height: 1.5;
        }
        .ff-info-row svg {
          color: #b8001f;
          margin-top: 3px;
          flex-shrink: 0;
        }
        .ff-address-text {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: 2.6rem;
          line-height: 1.4;
        }
        .ff-phone-link {
          color: #b8001f;
          font-weight: 600;
          text-decoration: none;
        }
        .ff-phone-link:hover { text-decoration: underline; }

        .ff-call-btn {
          display: block;
          width: 100%;
          margin-top: auto;
          padding: 11px 0;
          background: linear-gradient(135deg, #7b0000, #b8001f);
          color: #fff;
          font-weight: 700;
          font-size: 0.9rem;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          letter-spacing: 0.04em;
          transition: opacity 0.2s, transform 0.15s;
          text-align: center;
          text-decoration: none;
        }
        .ff-call-btn:hover {
          opacity: 0.9;
          transform: scale(0.99);
          color: #fff;
        }

        /* ── Empty state ─────────────────────────────────────────── */
        .ff-empty {
          text-align: center;
          padding: 60px 20px;
          max-width: 400px;
          margin: 0 auto;
        }
        .ff-empty svg {
          color: #ccc;
          margin-bottom: 16px;
        }
        .ff-empty h3 { color: #555; font-size: 1.1rem; margin: 0 0 6px; }
        .ff-empty p { color: #999; font-size: 0.9rem; margin: 0; }

        /* ── Loading spinner ─────────────────────────────────────────── */
        .ff-loading {
          text-align: center;
          padding: 50px 20px;
          color: #999;
          font-size: 0.95rem;
        }
        .ff-spinner {
          width: 44px;
          height: 44px;
          border: 4px solid #f0e2d6;
          border-top-color: #b8001f;
          border-radius: 50%;
          animation: ff-spin 0.75s linear infinite;
          margin: 0 auto 16px;
        }
        @keyframes ff-spin {
          to { transform: rotate(360deg); }
        }

        /* ── Pagination ─────────────────────────────────────────── */
        .ff-pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          margin-top: 40px;
          flex-wrap: wrap;
        }
        .ff-page-btn {
          min-width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 14px;
          border: 1.5px solid #e0cfc8;
          background: #fff;
          color: #7b0000;
          border-radius: 10px;
          font-weight: 700;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .ff-page-btn:hover:not(:disabled) {
          border-color: #b8001f;
          background: #fffaf7;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(184,0,31,0.1);
        }
        .ff-page-btn.active {
          background: linear-gradient(135deg, #7b0000, #b8001f);
          color: #fff;
          border-color: transparent;
          box-shadow: 0 4px 15px rgba(123,0,0,0.25);
        }
        .ff-page-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
          border-color: #eee;
        }
      `}</style>

      <div className="ff-page">
        {/* ── Hero ── */}
        <div className="ff-hero">
          <img
            className="ff-hero-bg"
            src="https://images.unsplash.com/photo-1610375461246-83df859d849d?w=1400&q=85"
            alt="Greenheap Gold Franchise Store"
          />
          <div className="ff-hero-overlay" />
          <div className="ff-hero-content">
            <div className="ff-hero-icon">
              <MdStorefront size={32} color="#ffd700" />
            </div>
            <h1>Find the Nearby <span>Franchise</span></h1>
            <p>Discover Greenheap Gold franchise stores near you. We bring gold closer to your doorstep.</p>
          </div>
        </div>

        {/* ── Filter Card ── */}
        <div className="container">
          <div className="ff-filter-card">
            <div>
              <label className="ff-filter-label" htmlFor="stateSelect">Select State</label>
              <select
                id="stateSelect"
                className="ff-select"
                value={selectedState}
                onChange={handleStateChange}
                disabled={statesLoading}
              >
                <option value="">{statesLoading ? "Loading states..." : "-- Select State --"}</option>
                {states.map((s) => (
                  <option key={s.id} value={s.id}>{s.state}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="ff-filter-label" htmlFor="citySelect">Select City</label>
              <select
                id="citySelect"
                className="ff-select"
                value={selectedCity}
                onChange={handleCityChange}
                disabled={!selectedState || citiesLoading}
              >
                <option value="">
                  {!selectedState
                    ? "-- Select State First --"
                    : citiesLoading
                    ? "Loading cities..."
                    : "-- Select City --"}
                </option>
                {cities.map((c) => (
                  <option key={c.id} value={c.id}>{c.city}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* ── Step prompts ── */}
        {!selectedState && !statesLoading && (
          <div className="ff-empty">
            <MdLocationOn size={60} style={{ color: "#c8a96e" }} />
            <h3>Select a State to Begin</h3>
            <p>Choose your state from the dropdown above to discover nearby Greenheap Gold franchise stores.</p>
          </div>
        )}



        {/* ── Results ── */}
        {selectedState && (
          <>
            <div className="ff-section-header">
              <h2 className="ff-section-title">
                {selectedCity && cities.length > 0 ? (
                  <>
                    Franchises in{" "}
                    <span style={{ color: "#b8001f" }}>
                      {cities.find((c) => String(c.id) === String(selectedCity))?.city || "this area"}
                    </span>
                    ,{" "} {states.find((s) => String(s.id) === String(selectedState))?.state || ""}
                  </>
                ) : (
                  <>
                    All Franchises in{" "}
                    <span style={{ color: "#b8001f" }}>
                      {states.find((s) => String(s.id) === String(selectedState))?.state || ""}
                    </span>
                  </>
                )}
              </h2>
              {!franchisesLoading && (
                <span className="ff-count-badge">
                  {pagination?.total || franchises.length} Found
                </span>
              )}
            </div>

            {franchisesLoading ? (
              <div className="ff-loading">
                <div className="ff-spinner" />
                <p>Finding franchises near you...</p>
              </div>
            ) : franchises.length > 0 ? (
              <>
                <div className="ff-grid">
                  {franchises.map((f) => (
                    <div className="ff-card" key={f.id}>
                      {/* Card Image */}
                      <div className="ff-card-img-wrap">
                        {f.avatar ? (
                          <img
                            src={f.avatar}
                            alt={f.franchise_name}
                            onError={(e) => (e.currentTarget.src = "/assets/images/green1.png")}
                          />
                        ) : (
                          <div className="ff-logo-placeholder">
                            <img
                              src="/assets/images/green1.png"
                              alt="Greenheap Gold"
                              className="ff-logo-img"
                            />
                          </div>
                        )}
                        <span className="ff-city-pill">
                          <MdLocationOn size={11} style={{ verticalAlign: "middle", marginRight: 3 }} />
                          {f.address?.city || ""}
                        </span>
                        {f.verified === 1 && (
                          <span className="ff-verified-badge">
                            <MdVerified size={12} style={{ verticalAlign: "middle", marginRight: 3 }} />
                            Verified
                          </span>
                        )}
                      </div>

                      {/* Card Body */}
                      <div className="ff-card-body">
                        <p className="ff-card-name">
                          <MdStorefront size={18} />
                          {f.franchise_name || f.name}
                        </p>

                        <div className="ff-info-row">
                          <MdLocationOn size={16} />
                          <span className="ff-address-text">
                            {f.address?.franchise_address
                              ? `${f.address.franchise_address}, ${f.address.city}, ${f.address.state} – ${f.address.postal}`
                              : `${f.address?.city || ""}, ${f.address?.state || ""}`}
                          </span>
                        </div>

                        <div className="ff-info-row">
                          <MdLanguage size={16} />
                          {f.url ? (
                            <a
                              href={f.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#b8001f",
                                fontWeight: "500",
                                textDecoration: "none",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "200px",
                                display: "inline-block",
                              }}
                              title={f.url}
                            >
                              {new URL(f.url).hostname}
                            </a>
                          ) : (
                            <span style={{ color: "#999" }}>N/A</span>
                          )}
                        </div>

                        <div className="ff-info-row">
                          <MdPhone size={16} />
                          <a className="ff-phone-link" href={`tel:${f.franchise_phone || f.phone_number}`}>
                            {f.franchise_phone || f.phone_number}
                          </a>
                        </div>
                        
                        <a className="ff-call-btn" href={`tel:${f.franchise_phone || f.phone_number}`} style={{ marginTop: '8px' }}>
                           📞 Call Store
                        </a>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination UI */}
                {pagination && pagination.last_page > 1 && (
                  <div className="ff-pagination">
                    <button
                      className="ff-page-btn"
                      disabled={currentPage === 1}
                      onClick={() => { setCurrentPage(p => p - 1); window.scrollTo(0, 400); }}
                    >
                      Prev
                    </button>

                    {[...Array(pagination.last_page)].map((_, i) => {
                      const pg = i + 1;
                      if (pagination.last_page > 7) {
                        if (pg !== 1 && pg !== pagination.last_page && (pg < currentPage - 1 || pg > currentPage + 1)) {
                          if (pg === currentPage - 2 || pg === currentPage + 2) return <span key={pg} style={{color: '#7b0000'}}>...</span>;
                          return null;
                        }
                      }
                      return (
                        <button
                          key={pg}
                          className={`ff-page-btn ${currentPage === pg ? "active" : ""}`}
                          onClick={() => { setCurrentPage(pg); window.scrollTo(0, 400); }}
                        >
                          {pg}
                        </button>
                      );
                    })}

                    <button
                      className="ff-page-btn"
                      disabled={currentPage === pagination.last_page}
                      onClick={() => { setCurrentPage(p => p + 1); window.scrollTo(0, 400); }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="ff-empty">
                <MdStorefront size={60} />
                <h3>No Franchises Found</h3>
                <p>There are no registered franchises in this area yet. Try a different city.</p>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

     

